/*
 * WHO THIS LIBRARY GOT EACH TEXT FROM, NAMED ONCE.
 *
 * `pipeline/lib/licence-witness.ts` maps ten suppliers to every place they state terms, and
 * `pipeline/licence-drift.ts` re-reads them. So this project can answer *has a supplier changed
 * what it says*. Until this file existed it could not answer the question that follows — **which
 * shelves does that touch** — because nothing linked a corpus to whoever supplied it.
 *
 * Measured 2026-08-18, before this was added: of 33 licence declarations in `corpora.ts`, **26 read
 * "Public domain" and named nobody**, and only two corpora named a supplier at all. "Public domain"
 * is a true statement and not a provenance — it says the text is free and nothing about where this
 * project got it.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────
 * THE IDS LIVE IN THE SCHEMA, FOR THE REASON `report-sources.ts` ALREADY GIVES.
 *
 * That file says it plainly: *"The ids live here because `roster.ts` cannot import from the pipeline
 * and the pipeline must not invent names the roster then guesses at."* The same applies with the
 * same force. `corpora.ts` cannot import from `pipeline/`, so if the supplier names were declared
 * beside the licence checks, every corpus would be spelling them from memory and nothing would
 * notice a typo.
 *
 * So they are declared here, `corpora.ts` references them, and `licence-witness.ts` is checked
 * against them by its own test. Neither side owns a name the other cannot see.
 */

/** A party this library takes text from. */
export interface Supplier {
  id: string;
  /** As a reader would name them on the page. */
  name: string;
  /**
   * Why this party rather than the text's author.
   *
   * A supplier is whoever this project actually fetched from, which is often not who wrote,
   * translated or first digitised the text — and the difference is where most of the licence
   * trouble in this library has been found.
   */
  note?: string;
}

export const SUPPLIERS: readonly Supplier[] = [
  {
    id: 'ebible',
    name: 'eBible.org',
    note: 'Publishes per-translation terms across more than a thousand language codes; watched by pipeline/lib/ebible.ts.',
  },
  {
    id: 'crosswire',
    name: 'CrossWire Bible Society',
    note: 'Licenses per module. 80 of its 427 modules grant only to CrossWire, which is no grant downstream — so what is taken from here is Public Domain only.',
  },
  {
    /*
     * SEPARATE FROM eBIBLE ON PURPOSE, though its files arrive through eBible.
     *
     * This registry's own note says a supplier is "whoever this project actually fetched from,
     * which is often not who wrote, translated or first digitised the text — and the difference is
     * where most of the licence trouble in this library has been found." Biblica is the case where
     * the difference is the licence itself: eBible's catalogue column carries Biblica's copyright
     * NOTICE, and the CC BY-SA grant is Biblica's own statement elsewhere. Filing these two under
     * `ebible` would have pointed a reader at a page that does not contain the permission.
     */
    id: 'biblica',
    name: 'Biblica, Inc.',
    note: 'Rights holder of the Ketab El Hayat Arabic Bible and the Hebrew Living New Testament, both CC BY-SA 4.0 through its Open editions programme. Distributed by eBible; the grant is Biblica’s and is watched at both, because the catalogue column states a copyright notice rather than a licence. Its trademark must be removed from a derivative work, which is why neither text is registered under the supplier’s own title — see pipeline/lib/biblica.ts.',
  },
  {
    id: 'berean',
    name: 'Berean Bible / Bible Hub',
    note: 'Watched by pipeline/lib/berean.ts.',
  },
  {
    id: 'sefaria',
    name: 'Sefaria',
    note: 'States no global licence and says so: each text is licensed separately and the per-version field governs. That is what fetch-sefaria.ts reads.',
  },
  {
    id: 'coptic-scriptorium',
    name: 'Coptic SCRIPTORIUM',
    note: 'CC-BY 4.0 in the header of every file. The Coptic itself is credited to Marcion and Hany Takla, so the grant is an annotator’s over a text somebody else keyed.',
  },
  {
    id: 'quranenc',
    name: 'QuranEnc',
    note: 'Republication on seven stated conditions. Watched by pipeline/lib/quranenc.ts.',
  },
  {
    id: 'hadeethenc',
    name: 'HadeethEnc',
    /*
     * THE GAP THIS NOTE EXISTED FOR IS CLOSED IN WRITING — 2026-08-22.
     *
     * It read: the seven conditions "are published on quranenc.com and NOWHERE on hadeethenc.com.
     * Separate from `quranenc` precisely so that gap is visible on the shelf it affects." That was
     * a fact about two websites, and the right way to settle it was to ask the party behind both.
     *
     * Asked, and answered by Abdullah Radman for IslamHouse.com: **"The usage conditions applied to
     * our Islamic content resources also apply to"** the other content they publish. So the terms
     * are one set across their platforms, stated by them rather than inferred by us.
     *
     * The same reply confirmed the methodology sentence this library shows a reader — *"HadeethEnc
     * publishes under the methodology of Ahl-us-Sunnah wal-Jamaaʿah"* — as accurately reflecting
     * their approach, which was asked because a disclosure nobody has checked is a guess.
     */
    note: 'Run by the institution behind QuranEnc, and the seven conditions are published on quranenc.com and NOWHERE on hadeethenc.com — still true of the two sites. What changed on 2026-08-22 is that IslamHouse answered the question in writing: one set of conditions covers their content across platforms. Separate from `quranenc` precisely so the published gap stays visible on the shelf it affects.',
  },
  {
    id: 'islamhouse',
    name: 'IslamHouse.com',
    /*
     * GRANTED IN WRITING, 2026-08-22, BY THE PARTY THAT PUBLISHES IT — and this is the first
     * permission this project has been given for a text whose own site refused one by silence.
     *
     * Riyāḍ al-Ṣāliḥīn's site states no terms of use and carries `جميع الحقوق محفوظة` — all rights
     * reserved. `hadith-source.ts` accordingly told readers the book is here only as a Wikisource
     * fragment, "at 399 reports of some 1,900". Asked directly, IslamHouse extended their content
     * conditions to it.
     *
     * THE CONDITIONS, AS THEY WROTE THEM, because a grant summarised is a grant misremembered:
     *
     *   - the original content remains unchanged
     *   - proper attribution and source references are displayed
     *   - available edition, publisher, author and other metadata is preserved
     *   - the content is not sold, restricted behind paid access, or used as a standalone
     *     commercial database
     *
     * Every one of those this library already does by construction — `texts are never modified` is
     * an older rule of Ariel's, and the publishing gate exists to keep text out of paid features.
     * **The fourth condition is the one to hold onto**: it forbids a bulk download or dataset
     * release, which is a thing this project does not do and could drift into.
     *
     * AND THERE IS NO API FOR IT. They said so plainly: the Riyāḍ content "may be accessed through
     * the available website resources". So this is a scrape under permission rather than a feed —
     * which is a fetch-stage problem and not a licence one.
     */
    note: 'The publisher of Riyāḍ al-Ṣāliḥīn, and the institution behind QuranEnc and HadeethEnc. Granted this project use of the Riyāḍ content in writing on 2026-08-22 under their standard conditions: unchanged, attributed, metadata preserved, and never sold, paywalled or released as a standalone database. No API exists for it — the site is the only route.',
  },
  {
    id: 'islamware',
    name: 'Islam Ware, mirrored by Hendy Irawan',
    note: 'Asserts copyright over its 2006–2014 keying. Taken under Feist rather than by grant — a faithful transcription of a ninth-century text earns nothing.',
  },
  {
    id: 'thaqalayn',
    name: 'The Thaqalayn Project',
    note: 'Its root LICENSE is CC0 and its API.md restricts use to education and research. The friendlier was acted on; see pipeline/lib/thaqalayn-books.ts.',
  },
  {
    id: 'athar',
    name: 'Athar, an open hadith narrator-transmission corpus — Haitham A. El-Ghareeb, Mansoura University',
    note: 'CC-BY 4.0 at 10.5281/zenodo.21005942, and the only licence this project has found over minority-branch hadith that is neither non-commercial nor share-alike — every other route runs through OpenITI, refused twice over. Attribution is required, and is written into every row rather than only printed. Unlike Islam Ware this is a grant and not a Feist argument.',
  },
  {
    id: 'ibnabishaybah',
    name: 'The Muṣannaf of Ibn Abī Shayba, digitised at github.com/muxaibest/ibnabishaybah',
    note: 'CC0 1.0, and the two halves rest on different grounds — which is why the repository states them separately. The Arabic is public domain by age: Ibn Abī Shayba died in 849. The ENGLISH is a living person’s and is dedicated rather than expired — "Sheikh Abu Sultana gave permission to release it under CC0 1.0 Universal" — so it is a grant and not a Feist argument, unlike Islam Ware. This project takes the Arabic only, because nobody has said whether that English was written or generated and a machine translation must not be able to pass as an edition. The dataset mirror at huggingface.co/datasets/freococo/musannaf_ibn_abi_shaybah reports the same terms; the repository is read instead, so the text and the terms come from one place.',
  },
  {
    id: 'mustadrak-export',
    name: 'Mustadrak al-Wasāʾil, exported from a Persian digital library at github.com/huacheakedanc/mustadrak-al-wasail',
    note: 'STATES NO LICENCE, and does not need to: al-Nūrī al-Ṭabarsī died in 1902, so the work is public domain by age, and a faithful transcription of a fixed text earns no copyright of its own — the same basis as the Sunni six. Unlike Usul, refused as "unattributed as well as ungranted", this export NAMES its edition: Muʾassasat Āl al-Bayt li-Iḥyāʾ al-Turāth, Qom. That edition is modern and its footnotes are its editors’ work, so they are not taken — the digitiser marks every one with an anchor and a `پاورقی` line, 2,515 matched pairs in one volume, which makes leaving them exact rather than approximate. THE SAME ACCOUNT PUBLISHES BIḤĀR AL-ANWĀR AND THAT COPY IS REFUSED: it went through Persian typesetting, which has one yeh where Arabic has two, so ي and ى collapsed into ی — 33,969 against 27 survivors in a single volume — and «على» (upon) and «علي» (ʿAlī) became one ambiguous form, 4,556 times, with none of either left. Two repositories, one owner, and the letters decided it.',
  },
  {
    id: 'ar-wikisource',
    name: 'Arabic Wikisource — ar.wikisource.org',
    note: 'CC BY-SA 4.0, read from the site’s own `rightsinfo` endpoint rather than from a badge on a page, and re-read by the stage on every run. Share-alike binds ADAPTED material and this library alters nothing it carries, which is why this supplier became usable on 2026-08-18 and not before. The underlying works are public domain by age several times over — ʿAbd al-Razzāq died in 827 — so what the licence actually covers is the volunteers’ transcription, and that is the thing being credited. Fifty-one hadith books are held there; this project has taken one so far.',
  },
  {
    id: 'tanzil',
    name: 'Tanzil',
    note: 'The Arabic Quran, verbatim copying with attribution.',
  },
  {
    id: 'hadith-kg',
    name: 'hadith-kg — emadjumaah/hadith-kg on Hugging Face',
    note: 'CC BY 4.0, stated on the dataset. 425 books and 715,790 hadith in one 1.63 GB SQLite file, each book naming its printed edition — which is the thing most Arabic hadith datasets do not do, and the reason this supplier was worth 1.63 GB of B-tree descent to reach. Every compiler it carries died before 1500, so the text is public domain by age and what CC BY covers is the keying and the apparatus around it: the per-report grade, the numbering, and the taraf. The attribution that licence asks for is the dataset, and it is named on every shelf built from it.',
  },
  {
    id: 'he-wikisource',
    name: 'Hebrew Wikisource — he.wikisource.org, from Project Ben-Yehuda',
    note: 'The site states CC BY-SA 4.0, and the work under it is public domain by age: Ḥayyim Zvi Reckendorf died in 1875 and his Leipzig 1857 Quran is out of copyright everywhere on any reckoning. Project Ben-Yehuda permits any use, commercial included, of the works it marks נחלת הכלל. So what the site licence covers is the volunteers’ transcription, and what the text rests on is age — the same two-part reading applied to Arabic Wikisource and to the Old Georgian Bible.',
  },
  {
    id: 'betamasaheft',
    name: 'Beta maṣāḥǝft — the Hamburg Geʽez corpus',
    note: 'CC BY-SA 4.0 on every file, and that is NOT the whole statement, which is the thing a shelf-wide reading gets wrong. Seventeen works carry a further copyright notice inside the same `<licence>` element. Fourteen are Ran HaCohen’s transcriptions, which assert his copyright and add no restriction. Three are the 1995 Library of Ethiopian Texts notice of Michal Jerabek, permitting use "for any NONCOMMERCIAL purpose" and stating the text "cannot be sold under any circumstances" — which this library’s commercial gate refuses. Two of those three scope it to `edition 1` by name and their verse-addressed edition is a different one, so Enoch and Wisdom survive; the third names no edition, so that work is encumbered whole. The rule is per file and per edition rather than per corpus, and `pipeline/fetch-ethiopian.ts` applies it that way — a fetch that read the shelf licence once would have ingested two unsellable texts into a library that may be sold. Share-alike binds ADAPTED material and this library alters nothing it carries.',
  },
  {
    id: 'ogb',
    name: 'The Old Georgian Bible — archive.org item `ogb_20220326`',
    note: 'A born-digital EPUB of the Mtskheta manuscript Old Testament with Giorgi Mtatsmindeli’s eleventh-century New Testament. NOT published on the item’s Public Domain Mark, which is an uploader’s assertion rather than a grant and is demonstrably wrong on the same uploader’s `kaOrth`, the Patriarchate’s own modern translation. Published on age and Feist instead: Giorgi died in 1065 and no term reaches him, and keying a fixed text earns no copyright of its own — the identical basis on which this library carries the Sunni six. The archaic orthography is asserted on every run, because the uploader’s other item is a living church’s modern property and confusing the two would publish it.',
  },
  {
    id: 'brill',
    name: 'Brill',
    note: 'Nahj al-Balāgha, granted directly by the rightsholder.',
  },
  {
    id: 'scrollmapper',
    name: 'scrollmapper/bible_databases',
    note: 'The largest single supplier of renderings here — the King James, the ASV, the Douay-Rheims, the CPDV, the Church Slavonic and the Sahidic Coptic. Its repository is MIT, which is a software licence stretched over 140 translations and a host claim rather than a grant; it does not matter, because every text taken from it is free by age.',
  },
  {
    id: 'byztxt',
    name: 'byztxt/greektext-antoniades',
    note: 'Maurice A. Robinson’s edition of the Patriarchal Text — the Greek New Testament of the Orthodox churches.',
  },
  {
    id: 'biblicalhumanities',
    name: 'biblicalhumanities/Nestle1904',
    note: 'The Greek New Testament this library shows — Nestle 1904, out of copyright by age, with morphology.',
  },
  {
    id: 'gutenberg',
    name: 'Project Gutenberg',
  },
  {
    id: 'openscriptures',
    name: 'Open Scriptures (morphhb)',
    note: 'The Westminster Leningrad Codex — the Hebrew this library shows under every Hebrew Bible verse.',
  },
] as const;

/** Every id a corpus may point at. */
export const SUPPLIER_IDS: readonly string[] = SUPPLIERS.map(s => s.id);

/** Whether a name is one this project declares — for the guard on both sides. */
export const isSupplier = (id: string): boolean => SUPPLIER_IDS.includes(id);

/** How a supplier is named on the page, or undefined where the id is unknown. */
export const supplierName = (id: string): string | undefined =>
  SUPPLIERS.find(s => s.id === id)?.name;
